import type { ReactNode } from 'react';
import { Marca } from './Marca';
import { Escudo, Grafico, Reloj } from './iconos';

/**
 * Panel de marca de las pantallas de acceso.
 *
 * Solo aparece en escritorio: en teléfono la pantalla la resuelve el formulario
 * completo y este panel no existe (ver Login.tsx).
 *
 * ── Fondo ──
 * Es una composición en CSS: base marino, un resplandor naranja sangrado por la
 * esquina inferior derecha y una trama de puntos muy tenue. **Si existe el
 * archivo `web/public/panel-fondo.jpg`, se usa como fotografía de fondo** por
 * debajo de todo, con el marino encima al 82 % para que el texto siga
 * legible. Un archivo que no existe simplemente no se pinta: no rompe nada, así
 * que se puede activar dejando caer la imagen sin tocar código.
 *
 * ── Los destacados ──
 * Van en filas separadas por filetes de 1 px, no en tarjetas ni con pastillas de
 * fondo. Es lo que le da el aire de ficha técnica.
 *
 * El tercero va de último a propósito, que es donde se detiene la vista: **quien
 * va a responder tiene que leer la promesa de confidencialidad antes de escribir
 * nada.** No es relleno de marketing, es la condición que hace que el dato
 * sirva (D-009).
 */

type Destacado = { icono: ReactNode; titulo: string; detalle: string };

const DESTACADOS: Destacado[] = [
  {
    icono: <Reloj size={22} />,
    titulo: 'Antes, durante y después',
    detalle: 'Cada programa se mide en tres momentos, no en uno.',
  },
  {
    icono: <Grafico size={22} />,
    titulo: 'Resultados por competencia',
    detalle: 'Escucha activa, comunicación asertiva, manejo de conflictos.',
  },
  {
    icono: <Escudo size={22} />,
    titulo: 'Tus respuestas son confidenciales',
    detalle: 'Tu empresa ve los resultados del grupo, nunca lo que respondiste tú.',
  },
];

export function PanelPresentacion() {
  return (
    <aside className="relative hidden w-[58%] shrink-0 overflow-hidden bg-marino-800 lg:block">
      {/* Fotografía opcional. Si el archivo no existe, esta capa no pinta nada. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/panel-fondo.jpg')" }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-marino-800/[0.82]" />

      {/* Resplandor naranja, sangrado por la esquina inferior derecha. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -bottom-52 h-[36rem] w-[36rem] rounded-full opacity-30 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, var(--color-naranja-500) 0%, transparent 68%)',
        }}
      />
      {/* Luz fría arriba a la izquierda, para que el marino no quede plano. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-32 h-[30rem] w-[30rem] rounded-full opacity-40 blur-3xl"
        style={{
          background: 'radial-gradient(circle, var(--color-marino-600) 0%, transparent 70%)',
        }}
      />
      {/* Trama de puntos, muy tenue. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />

      <div className="relative flex h-full flex-col justify-between p-12 xl:p-16 2xl:p-20">
        <Marca tamano="lg" tono="claro" />

        <div className="max-w-xl">
          <h2 className="text-[2.6rem] leading-[1.12] font-bold text-balance text-white">
            Las personas se unen a los equipos como individuos, pero marcan la diferencia
            como equipo.
          </h2>

          <ul className="mt-12">
            {DESTACADOS.map((d) => (
              <li
                key={d.titulo}
                className="flex gap-5 border-t border-white/10 py-5 last:border-b"
              >
                <span className="mt-0.5 shrink-0 text-naranja-500">{d.icono}</span>
                <div>
                  <div className="text-[1.05rem] font-semibold text-white">{d.titulo}</div>
                  <div className="mt-1 text-sm leading-relaxed text-white/55">{d.detalle}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-white/35">
          Habilidades blandas medidas con datos · +5 años acompañando equipos
        </p>
      </div>
    </aside>
  );
}
